import { type ApolloError } from '@apollo/client';

import { GRAPHQL_ERROR_CODES } from 'src/data/graphqlErrorCodes';

import type { GraphQLEdges, PlatformGraphQLError } from './types';

export const getSingleProp = <T>(obj?: Record<string, T>): T | undefined =>
  obj ? Object.values(obj)[0] : undefined;

// Unwrap edges-node to simple 'data' array
export const unwrapEdges = <T>(edges?: GraphQLEdges<T>): T[] => {
  if (!edges) {
    return [];
  }

  return edges.map((edge) => edge.node);
};

export const stripGraphQLErrorPrefix = (error: string | Error | null): string => {
  const message = error ? (typeof error === 'string' ? error : error.message) : '';
  return (message || '').replace(/^GraphQL error:/, '').trim();
};

export const parseGraphQLError = (error: Error | Error[] | null): PlatformGraphQLError[] => {
  if (!error) {
    return [];
  }

  // Handle ApolloError with networkError
  const apolloError = error as ApolloError;
  if (apolloError?.networkError) {
    return [
      {
        message: `Network error: ${apolloError.networkError.message}`,
        code: GRAPHQL_ERROR_CODES.networkError,
      },
    ];
  }

  const errorsNode = Array.isArray(error)
    ? // Errors thrown directly from query
      error
    : // Mutation errors
      apolloError?.graphQLErrors;

  if (errorsNode?.length > 0) {
    return errorsNode.map((gqlError: unknown) => {
      const error = gqlError as {
        message: string;
        extensions?: { code?: string; error_properties?: Record<string, unknown> };
      };
      return {
        message: error.message,
        code: error?.extensions?.code || GRAPHQL_ERROR_CODES.undefined,
        errorProperties: error?.extensions?.error_properties,
      };
    });
  }

  // Handle case where error is not an array and doesn't have graphQLErrors
  if (!Array.isArray(error) && 'message' in error) {
    return [
      {
        message: error.message,
        code: GRAPHQL_ERROR_CODES.undefined,
      },
    ];
  }

  return [];
};

export const doesParsedGraphQLErrorContainErrorCode = ({
  parsedError,
  errorCode,
}: {
  parsedError: PlatformGraphQLError[];
  errorCode: string;
}): boolean => {
  return parsedError.some((error) => error.code === errorCode);
};

export const getParsedGraphQLFirstError = (
  error: Error | Error[] | null
): PlatformGraphQLError | undefined => {
  const parsedErrors = parseGraphQLError(error);
  return parsedErrors[0];
};

const takeErrorMessage = (error: { message: string }): string => error.message;

export const parseErrors = (error: ApolloError | null): string[] => {
  if (!error) {
    return [];
  }

  if (error.networkError) {
    return [`Network error: ${error.networkError.message}`];
  }
  if (error.graphQLErrors && error.graphQLErrors.length > 0) {
    return error.graphQLErrors.map(takeErrorMessage);
  }
  return [error.message];
};

export const throwIfResultHasErrors = (result: { errors?: readonly Error[] }): void => {
  const errors = result?.errors || [];
  if (errors.length > 0) {
    // TODO: consider changing to
    //   const merged = errors.map(takeErrorMessage).join('\n');
    //   throw new BackendError(merged);
    throw errors;
  }
};
